import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import toast from 'react-hot-toast';
import db from '../data/db';
import { syncService } from '../data/syncService';
import { useAuth } from './AuthContext';
import { useSubscription } from './SubscriptionContext';
import useAppStore from '../store/useAppStore';
import { buildContext, evaluateNewUnlocks } from '../utils/badgeEngine';
import { getBadge } from '../data/badges';

const AppContext = createContext();

export function useApp() {
  return useContext(AppContext);
}

function getToday() {
  return new Date().toISOString().split('T')[0];
}

// Pure: given the previous streak record and today's context, compute the
// next streak. Extracted so recordStudySession can use the result directly
// for badge evaluation without racing against React's async setState.
function computeNextStreak(prev, today, yesterdayStr, premium) {
  if (prev.lastStudyDate === today) return prev;

  let next = { ...prev, showFreezeUpsell: false };

  if (prev.lastStudyDate === yesterdayStr || prev.lastStudyDate === null) {
    next.current = prev.current + 1;
  } else if (prev.lastStudyDate && premium) {
    next.current = prev.current + 1;
  } else if (prev.lastStudyDate && prev.freezesAvailable > 0 && !prev.freezeUsedThisWeek) {
    next.current = prev.current + 1;
    next.freezesAvailable = prev.freezesAvailable - 1;
    next.freezeUsedThisWeek = true;
  } else if (prev.lastStudyDate && prev.current > 0) {
    next.showFreezeUpsell = true;
    next.streakBeforeBreak = prev.current;
    next.current = 1;
  } else {
    next.current = 1;
  }

  next.longest = Math.max(next.longest, next.current);
  next.lastStudyDate = today;
  return next;
}

export function AppProvider({ children }) {
  const [streak, setStreak] = useState({
    current: 0,
    longest: 0,
    lastStudyDate: null,
    freezesAvailable: 1,
    freezeUsedThisWeek: false,
    showFreezeUpsell: false,
    streakBeforeBreak: 0,
  });
  const [hasOnboarded, setHasOnboarded] = useState(false);
  // theme is now sourced from useAppStore (Zustand persist) per
  // shared-docs/frontend-architecture.md. AppContext keeps the proxy
  // surface (`theme`, `toggleTheme`) so consumers don't have to migrate.
  const nightMode = useAppStore((s) => s.nightMode);
  const setNightMode = useAppStore((s) => s.setNightMode);
  const toggleNightModeStore = useAppStore((s) => s.toggleNightMode);
  const theme = nightMode ? 'dark' : 'light';
  const [loading, setLoading] = useState(true);
  const [syncStatus, setSyncStatus] = useState('idle');
  const { isAuthenticated } = useAuth();
  const { isPremium } = useSubscription();
  const hasRunInitialSync = useRef(false);

  // Load settings from database on mount
  useEffect(() => {
    async function loadSettings() {
      try {
        const streakData = await db.settings.get('streak');
        if (streakData) setStreak(streakData.value);

        const onboardedData = await db.settings.get('onboarded');
        if (onboardedData) setHasOnboarded(onboardedData.value);

        // One-time migration: lift legacy Dexie theme into Zustand,
        // then forget about Dexie for theme going forward.
        const themeData = await db.settings.get('theme');
        if (themeData?.value === 'light') setNightMode(false);
        if (themeData?.value === 'dark') setNightMode(true);
      } catch (e) {
        console.error('Failed to load settings:', e);
      }
      setLoading(false);
    }
    loadSettings();
    // setNightMode is a stable Zustand setter, but eslint doesn't know that.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Apply theme to document. Both signals are kept in sync:
  //   - data-theme="dark|light" drives the existing CSS-variable cascade
  //   - .dark class on <html> drives Tailwind's class-based darkMode
  // (per shared-docs/dark-mode.md — MUST be on documentElement).
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    document.documentElement.classList.toggle('dark', theme === 'dark');
  }, [theme]);

  // Subscribe to sync status changes
  useEffect(() => {
    const unsubscribe = syncService.onStatusChange(setSyncStatus);
    return unsubscribe;
  }, []);

  // Start/stop periodic sync based on auth state
  useEffect(() => {
    if (isAuthenticated) {
      if (!hasRunInitialSync.current) {
        hasRunInitialSync.current = true;
        syncService.initialSync();
      }
      syncService.startPeriodicSync();
    } else {
      syncService.stopPeriodicSync();
      hasRunInitialSync.current = false;
    }

    return () => syncService.stopPeriodicSync();
  }, [isAuthenticated]);

  const triggerSync = useCallback(() => {
    if (isAuthenticated) {
      syncService.syncAll();
    }
  }, [isAuthenticated]);

  const dismissFreezeUpsell = useCallback(() => {
    setStreak(prev => {
      const updated = { ...prev, showFreezeUpsell: false };
      db.settings.put({ key: 'streak', value: updated });
      return updated;
    });
  }, []);

  const updateStreak = useCallback(async (premiumOverride) => {
    const today = getToday();
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayStr = yesterday.toISOString().split('T')[0];

    const premium = typeof premiumOverride === 'boolean' ? premiumOverride : isPremium;

    setStreak(prev => {
      const next = computeNextStreak(prev, today, yesterdayStr, premium);
      if (next !== prev) db.settings.put({ key: 'streak', value: next });
      return next;
    });
  }, [isPremium]);

  const completeOnboarding = useCallback(async () => {
    setHasOnboarded(true);
    await db.settings.put({ key: 'onboarded', value: true });
  }, []);

  const toggleTheme = useCallback(() => {
    toggleNightModeStore();
  }, [toggleNightModeStore]);

  const recordStudySession = useCallback(async (cardsStudied, minutesStudied, correctCount) => {
    const today = getToday();
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayStr = yesterday.toISOString().split('T')[0];

    const existing = await db.stats.get(today);

    const priorLastDate = streak.lastStudyDate;
    const daysSinceLastStudy = priorLastDate
      ? Math.floor((new Date(today) - new Date(priorLastDate)) / 86400000)
      : 0;

    if (existing) {
      await db.stats.update(today, {
        cardsStudied: existing.cardsStudied + cardsStudied,
        minutesStudied: existing.minutesStudied + minutesStudied,
        correctCards: existing.correctCards + correctCount,
        sessions: existing.sessions + 1,
        dirty: 1,
      });
    } else {
      await db.stats.put({
        date: today,
        cardsStudied,
        minutesStudied,
        correctCards: correctCount,
        sessions: 1,
        dirty: 1,
      });
    }

    // Compute the new streak synchronously and persist it before evaluating
    // badges — otherwise the React setState/Dexie put inside updateStreak
    // would race with buildContext reading the streak back.
    const nextStreak = computeNextStreak(streak, today, yesterdayStr, isPremium);
    if (nextStreak !== streak) {
      await db.settings.put({ key: 'streak', value: nextStreak });
      setStreak(nextStreak);
    }

    try {
      const { unlockedBadges, unlockBadges } = useAppStore.getState();
      const ctx = await buildContext({
        streak: nextStreak,
        session: {
          cardsReviewed: cardsStudied,
          correctCount,
          hadPriorStudy: !!priorLastDate,
          daysSinceLastStudy,
          hour: new Date().getHours(),
        },
      });
      const newIds = evaluateNewUnlocks(ctx, unlockedBadges);
      if (newIds.length > 0) {
        unlockBadges(newIds);
        for (const id of newIds) {
          const badge = getBadge(id);
          if (badge) {
            toast.success(`${badge.icon} ${badge.name} — ${badge.description}`, { duration: 5000 });
          }
        }
      }
    } catch (err) {
      console.error('Badge evaluation failed:', err);
    }

    triggerSync();
  }, [streak, isPremium, triggerSync]);

  const value = {
    streak,
    hasOnboarded,
    theme,
    loading,
    syncStatus,
    updateStreak,
    dismissFreezeUpsell,
    completeOnboarding,
    toggleTheme,
    recordStudySession,
    triggerSync,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}
